import { apiClient } from '../../core/api';
import { useAuthStore } from '../../../stores/authStore';
import type {
  CancelPlanRequest,
  CancelPlanResponse,
  CreatePaymentRequest,
  CreatePaymentResponse,
  CreateSetupIntentRequest,
  CreateSetupIntentResponse,
  CreateTrialRequest,
  CreateTrialResponse,
  PaymentError,
  VisiblePlansResponse,
} from '../../../types/payment';
import { normalizeError, ErrorFactory } from '../../utils/errorHandling';
import type { AppError } from '../../../types/errors';

class PaymentApiClient {
  async createSetupIntent(priceId: string, email?: string): Promise<CreateSetupIntentResponse> {
    try {
      // Get user email from auth store if not provided
      let userEmail = email;
      if (!userEmail) {
        const authState = useAuthStore.getState();
        if (!authState.isAuthenticated || !authState.user?.email) {
          throw new Error('User must be authenticated to create payment');
        }
        userEmail = authState.user.email;
      }

      const request: CreateSetupIntentRequest = {
        email: userEmail,
        priceId,
      };

      const response = await apiClient.post('/payment/setup-intent', request);

      // Check for successful response without errorCode
      if (response.status === 200 && !(response.data as any)?.errorCode) {
        const data = response.data as any;

        // Validate response structure
        if (!data?.id || !data?.priceId || !data?.clientSecret) {
          throw new Error('Invalid response format from setup intent endpoint');
        }

        return {
          id: data.id,
          priceId: data.priceId,
          clientSecret: data.clientSecret,
        };
      }

      // Handle error response
      throw new Error((response.data as any)?.message || 'Failed to create setup intent');
    } catch (error) {
      const appError = normalizeError(error);
      console.error('Setup intent creation failed:', appError);
      throw appError;
    }
  }

  async createTrial(
    request: CreateTrialRequest,
    isYearlyTrial: boolean
  ): Promise<CreateTrialResponse> {
    try {
      // Build query parameters
      const queryParams = new URLSearchParams({
        isTrial: 'true',
        isYearlyTrial: isYearlyTrial.toString(),
      });

      const response = await apiClient.post(`/payment/create?${queryParams.toString()}`, request);

      // Check for successful response
      if (response.status === 200 && !(response.data as any)?.errorCode) {
        const data = response.data as any;

        return {
          success: true,
          subscriptionId: data.subscriptionId,
          customerId: data.customerId,
          trialEnd: data.trialEnd,
          message: data.message,
        };
      }

      // Handle error response with errorCode
      throw new Error((response.data as any)?.message || 'Failed to create trial subscription');
    } catch (error) {
      const appError = normalizeError(error);
      console.error('Trial creation failed:', appError);
      throw appError;
    }
  }

  async createPayment(request: CreatePaymentRequest): Promise<CreatePaymentResponse> {
    try {
      const response = await apiClient.post('/payment/create', request);

      // Check for successful response
      if (response.status === 200 && !(response.data as any)?.errorCode) {
        const data = response.data as any;

        return {
          success: true,
          subscriptionId: data.subscriptionId,
          customerId: data.customerId,
          message: data.message,
        };
      }
      throw new Error((response.data as any)?.message || 'Failed to create subscription');
    } catch (error) {
      const appError = normalizeError(error);
      console.error('Payment creation failed:', appError);
      throw appError;
    }
  }

  async cancelPlan(email: string, request: CancelPlanRequest): Promise<CancelPlanResponse> {
    try {
      const response = await apiClient.post(`/payment/cancel/${email}`, request);

      // Check for successful response
      if (response.status === 204) {
        const data = response.data as any;

        return {
          reason: data.reason,
          comment: data.comment,
          errorCode: data.errorCode,
          errorMessage: data.errorMessage,
        };
      }

      throw new Error('Failed to cancel plan');
    } catch (error) {
      const appError = normalizeError(error);
      console.error('Plan cancellation failed:', appError);
      throw appError;
    }
  }

  /**
   * Price Plan v2: fetch the cohort-aware visible plan list for a user.
   * GET /v1/billing/visible-plans/{userId}
   */
  async getVisiblePlans(userId: string): Promise<VisiblePlansResponse> {
    try {
      const response = await apiClient.get(`/billing/visible-plans/${userId}`);
      return response.data as VisiblePlansResponse;
    } catch (error) {
      const appError = normalizeError(error);
      console.error('Failed to fetch visible plans:', appError);
      throw appError;
    }
  }
}

export const paymentApi = new PaymentApiClient();
